import React, { useEffect, useCallback, useRef } from 'react';
import { connect } from 'react-redux';
import { AgGridReact } from 'ag-grid-react';
import PropTypes from 'prop-types';
import * as Sentry from '@sentry/react';
import { catalogService } from 'services';
import { getPromotionFields, getValueOfAttribut, store } from 'helpers';
import ProductCard from 'components/ProductCard';
import FilterTagBar from 'components/FilterTagBarV3/FilterTagBar';
import { selectScreenWidth } from 'selectors/ui';
import { selectTopSellingsItemsCount } from 'selectors/product';
import { selectCurrentPlatform } from 'selectors/platform';
import { size } from 'components/Core/Utils/Device';
import { aggridConstants } from 'constants/Aggrid.constants';
import { platformConstants } from 'constants/Platform.constants';
import { ProductCardWrapper, AgListWrapper } from './AggridListView.style';
import columnDefs from './columnDefinitions';
import localeText from '../locales';
import './AggridListView.scss';

const resizeGrid = (screenWidth, columnApi, gridApi) => {
  if (columnApi && gridApi) {
    // Check platform export flag
    const currentPlatform = selectCurrentPlatform(store.getState());
    const { attributs } = currentPlatform || {};
    const hasPlatformExport =
      Number(getValueOfAttribut(attributs || [], platformConstants.HAS_PLATFORM_EXPORT_KEY)) === 1;

    columnApi.setColumnVisible('agListNameCell', true);
    columnApi.setColumnVisible('agListReferenceCell', true);
    columnApi.setColumnVisible('agListPriceCell', true);
    columnApi.setColumnVisible('agListWeightVolumeCell', hasPlatformExport);
    columnApi.setColumnVisible('favoriteCell', true);
    columnApi.setColumnVisible('agListQuantityCell', true);
    columnApi.setColumnVisible('tabletCell', false);
    columnApi.setColumnVisible('mobileCell', false);

    if (screenWidth <= size.laptopL) {
      columnApi.setColumnVisible('agListReferenceCell', false);
    }
    if (screenWidth <= size.laptop) {
      columnApi.setColumnVisible('tabletCell', true);
      columnApi.setColumnVisible('agListNameCell', false);
      columnApi.setColumnVisible('agListReferenceCell', false);
      columnApi.setColumnVisible('agListPriceCell', false);
      columnApi.setColumnVisible('agListWeightVolumeCell', false);
    }
    if (screenWidth <= size.tabletR) {
      columnApi.setColumnVisible('tabletCell', false);
      columnApi.setColumnVisible('agListNameCell', true);
      columnApi.setColumnVisible('agListPriceCell', true);
    }
    if (screenWidth <= size.tablet) {
      columnApi.setColumnVisible('agListPriceCell', false);
      columnApi.setColumnVisible('agListWeightVolumeCell', false);
    }
    if (screenWidth <= size.mobileL) {
      columnApi.setColumnVisible('mobileCell', true);
      columnApi.setColumnVisible('tabletCell', false);
      columnApi.setColumnVisible('agListNameCell', false);
      columnApi.setColumnVisible('agListPriceCell', false);
      columnApi.setColumnVisible('agListWeightVolumeCell', false);
      columnApi.setColumnVisible('agListQuantityCell', false);
      columnApi.setColumnVisible('favoriteCell', false);
    }
    gridApi.sizeColumnsToFit();
  }
};

const AggridListView = ({
  onGridReady,
  onFirstDataRendered,
  onCellClicked,
  onFilterChanged,
  frameworkComponents,
  doesExternalFilterPass,
  isExternalFilterPresent,
  handleFilter,
  currentProduct,
  close,
  columnApi,
  gridApi,
  screenWidth,
  rowCount,
  totalRowCount,
  navigateToNextCell,
  searchValue,
  hasResults,
  hasTopSellingsItems,
  cartTotalQuantity,
  preorderCartTotalQuantity,
}) => {
  useEffect(() => {
    window.addEventListener('resize', resizeGrid(screenWidth, columnApi, gridApi));
  });

  const getRowHeight = () => {
    if (window.innerWidth <= size.mobileL) {
      return aggridConstants.LIST_MOBILE_ROW_HEIGHT;
    }

    return aggridConstants.LIST_ROW_HEIGHT;
  };

  const onColumnResized = (params) => {};

  const onColumnVisible = (params) => {};

  let productsToGetProperties = {};

  const updateCatalogProperties = useCallback((response) => {
    // Defensive guards: backend can return `null` or an unexpected shape.
    if (!Array.isArray(response)) {
      Sentry.captureMessage('Invalid response format in updateCatalogProperties', 'warning');
      productsToGetProperties = {};
      return;
    }

    response.forEach((item) => {
      if (!item) {
        return;
      }

      if (gridRef.current !== null && gridRef.current.api) {
        const rowNode = gridRef.current.api.getDisplayedRowAtIndex(item.aggrid_row_index);
        if (!rowNode || !rowNode.data) {
          return;
        }
        if (rowNode.data !== undefined && rowNode.data.already_get_properties !== true) {
          const rowToUpdate = rowNode.data;
          rowToUpdate.is_favorite = item.is_favorite;
          rowToUpdate.client_stock_quantity = item.client_stock_quantity;
          rowToUpdate.degressive_prices = item.degressive_prices;
          rowToUpdate.use_degressive_prices = item.use_degressive_prices;

          if (item.has_promotion && item.promotion != null) {
            rowToUpdate.applicable_price = item.applicable_price;
            rowToUpdate.applicable_price_vat = item.applicable_price_vat;
            rowToUpdate.has_promotion = item.has_promotion;
            rowToUpdate.promotion = Object.assign(
              rowToUpdate.promotion || {},
              getPromotionFields(item)
            );
          }

          rowToUpdate.already_get_properties = true;

          if (item.is_favorite || item.use_degressive_prices || item.has_promotion) {
            const row = gridRef.current.api.getDisplayedRowAtIndex(rowNode.rowIndex);
            if (row) {
              gridRef.current.api.redrawRows({ rowNodes: [row] });
            }
          }
          rowNode.setDataValue('favoriteCell', rowToUpdate.is_favorite);
          rowNode.updateData(rowToUpdate);
        }
      }
    });

    productsToGetProperties = {};
  });

  const componentProperties = {
    loading: (params) => {
      const state = store.getState();
      if (params.data !== undefined) {
        if (params.data.already_get_properties === true) {
          return null;
        }
      }
      if (params.data !== undefined) {
        productsToGetProperties[params.rowIndex] = {
          id: params.data.id,
          product_id: params.data.product_id,
          aggrid_row_index: params.rowIndex,
        };
      }
      if (
        params.rowIndex === params.node.rowRenderer.lastRenderedRow &&
        Object.keys(productsToGetProperties).length > 0
      ) {
        let productsToGetPropertiesTOsend = Object.values(productsToGetProperties).slice(-20);
        if (
          productsToGetPropertiesTOsend.length === 2 &&
          productsToGetPropertiesTOsend[0] &&
          productsToGetPropertiesTOsend[1] &&
          productsToGetPropertiesTOsend[0].aggrid_row_index === 0 &&
          productsToGetPropertiesTOsend[1].aggrid_row_index < 20
        ) {
          const productsToGetPropertiesNewtoSend = {};
          productsToGetPropertiesNewtoSend[0] = {
            id: productsToGetPropertiesTOsend[0].id,
            product_id: productsToGetPropertiesTOsend[0].product_id,
            aggrid_row_index: productsToGetPropertiesTOsend[0].aggrid_row_index,
          };
          const api = gridRef.current && gridRef.current.api;
          if (api) {
            for (let i = 0; i < productsToGetPropertiesTOsend[1].aggrid_row_index; i += 1) {
              const rowNode = api.getDisplayedRowAtIndex(i + 1);
              if (rowNode && rowNode.data && rowNode.rowIndex != null) {
                productsToGetPropertiesNewtoSend[rowNode.rowIndex] = {
                  id: rowNode.data.id,
                  product_id: rowNode.data.product_id,
                  aggrid_row_index: rowNode.rowIndex,
                };
              }
            }
          }
          productsToGetPropertiesTOsend = productsToGetPropertiesNewtoSend;
        }
        const { platform } = state;
        const filter = {
          platform: platform.selectedId,
        };
        catalogService
          .getCatalogPropertiesV4(filter, JSON.stringify(productsToGetPropertiesTOsend))
          .then(
            (response) => {
              updateCatalogProperties(response);
            },
            (err) => {
              return err;
            }
          );
      }
      return null;
    },
  };

  const rowHeight = getRowHeight();
  const gridRef = useRef(null);

  /**
   * Ag-Grid only re-renders cells when row data changes (or when explicitly asked).
   * The "Poids/Volume" column uses `getCartItem(...)` (global store) to compute totals from cart
   * quantity, so we must refresh that column when cart quantities change from the PDP (right panel).
   */
  useEffect(() => {
    if (!gridRef.current || !gridRef.current.api) {
      return;
    }
    gridRef.current.api.refreshCells({
      force: true,
      columns: ['agListWeightVolumeCell'],
    });
  }, [cartTotalQuantity, preorderCartTotalQuantity]);

  return (
    <div className="ag-list__container">
      <div className="ag-list__sub-container">
        <AgListWrapper className="ag-list__wrapper">
          <div className="AggridContainer ag-theme-custom-list">
            <FilterTagBar
              handleFilter={handleFilter}
              totalRowCount={totalRowCount}
              rowCount={rowCount}
            />
            {!hasResults && searchValue && searchValue.length >= 3 && (
              <>
                <p className="ag-list__notice">
                  Nous n'avons pas trouvé de produits correspondant votre recherche.
                </p>
                {hasTopSellingsItems && (
                  <p className="ag-list__title">
                    Consultez les produits tendances chez nos clients :
                  </p>
                )}
              </>
            )}
            <AgGridReact
              cacheBlockSize={100}
              columnDefs={columnDefs}
              components={componentProperties}
              doesExternalFilterPass={doesExternalFilterPass}
              frameworkComponents={frameworkComponents}
              handleFilter={handleFilter}
              isExternalFilterPresent={isExternalFilterPresent}
              loadingOverlayComponent="Spinner"
              localeText={localeText}
              maxConcurrentDatasourceRequests={2}
              navigateToNextCell={navigateToNextCell}
              onCellClicked={onCellClicked}
              onColumnResized={onColumnResized}
              onColumnVisible={onColumnVisible}
              onFilterChanged={onFilterChanged}
              onFirstDataRendered={onFirstDataRendered}
              overlayNoRowsTemplate={
                '<span style="padding: 10px; border: 2px solid #444; background: lightgoldenrodyellow">Aucun produit trouvé</span>'
              }
              onGridReady={onGridReady}
              pivotMode={false}
              ref={gridRef}
              rowHeight={rowHeight}
              rowModelType="infinite"
              rowSelection="single"
              suppressContextMenu
              searchValue={searchValue}
              suppressRowClickSelection
              tooltipShowDelay={0}
            />
          </div>
        </AgListWrapper>
        {screenWidth > size.tabletR && (
          <div className="ag-list__product-card-container">
            <div className="ag-list__product-card-container__border">
              {currentProduct && (
                <ProductCardWrapper className="ag-list__product-card-wrapper">
                  <ProductCard
                    product={currentProduct}
                    close={close}
                    handleFilter={handleFilter}
                    searchValue={searchValue}
                    source="aggridListView"
                  />
                </ProductCardWrapper>
              )}
              {!currentProduct && (
                <div className="product-card__no-product">Aucun article séléctionné</div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  screenWidth: selectScreenWidth(state),
  hasTopSellingsItems: selectTopSellingsItemsCount(state),
  cartTotalQuantity: (state.cart && state.cart.current && state.cart.current.total_quantity) || 0,
  preorderCartTotalQuantity:
    (state.cart && state.cart.currentPreorder && state.cart.currentPreorder.total_quantity) || 0,
});

AggridListView.propTypes = {
  onGridReady: PropTypes.func,
  onFirstDataRendered: PropTypes.func,
  onFilterChanged: PropTypes.func,
  frameworkComponents: PropTypes.object,
  doesExternalFilterPass: PropTypes.func,
  isExternalFilterPresent: PropTypes.func,
  handleFilter: PropTypes.func,
  currentProduct: PropTypes.object,
  close: PropTypes.func,
  gridApi: PropTypes.object,
  screenWidth: PropTypes.number,
  rowCount: PropTypes.number,
  navigateToNextCell: PropTypes.func,
  searchValue: PropTypes.string,
  hasTopSellingsItems: PropTypes.bool,
};

export default connect(mapStateToProps)(AggridListView);
